import { useQuery } from "@tanstack/react-query";
import { useServices } from "@/services/ServiceProvider";
import { useAuthStore } from "@/stores/useAuthStore";

export type { TelegramLinkResult } from "@/services/interfaces/IChannelService";

/** Keyed by the user: the link carries a nonce minted for the caller's own account. */
export const telegramLinkQueryKey = (userId: string | undefined) => ["telegram-link", userId] as const;

/**
 * The `t.me/<bot>?start=<nonce>` link, fetched *before* the user asks for it.
 *
 * Minting on click meant the click could not also navigate — the old flow had
 * to call window.open() from an effect once the response landed, which is not
 * a user gesture and so was popup-blocked often enough to need a QR rescue
 * panel underneath it. Prefetching turns Connect back into an ordinary link.
 *
 * Safe to call on every profile view: telegram-link-start returns the token
 * the user already holds until it is close to expiry, so this does not spend
 * the per-user cap on outstanding tokens.
 */
export function useTelegramLink(options?: { enabled?: boolean }) {
  const { channels } = useServices();
  const userId = useAuthStore((s) => s.session?.userId);
  return useQuery({
    queryKey: telegramLinkQueryKey(userId),
    queryFn: () => channels.startTelegramLink(),
    enabled: options?.enabled ?? true,
    // Tokens live 15 minutes and are re-served while >5 minutes remain, so a
    // refetch after 5 minutes is both cheap and guarantees the link a
    // returning user clicks still has time on it.
    staleTime: 5 * 60 * 1000,
    // Shown as text rather than retried: the cap answers 429, and silent
    // retries would spend it on a user who has not clicked anything yet.
    retry: false,
  });
}
